import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalNavigate, usePortalPath } from '@/hooks/usePortalPath';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
  actionLinkClassName,
} from '@core/theme/actionTheme.util';
import { AuthHeader } from '@/components/auth/AuthHeader';

const ResetPassword = () => {
  const portalNavigate = usePortalNavigate();
  const { path: portalPath } = usePortalPath();
  const location = useLocation();
  const { resetPassword } = useAuth();
  const email = location.state?.email;
  const [formState, setFormState] = useState({
    confirmationCode: '',
    newPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (!email) {
        setError('Email is required');
        return;
      }

      const result = await resetPassword({
        email,
        confirmationCode: formState.confirmationCode,
        newPassword: formState.newPassword,
      });

      if (result.success) {
        portalNavigate('/login');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!email) {
    return <Navigate to={portalPath('/forgot-password')} />;
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader
          title="Reset Password"
          subtitle="Enter the code sent to your email and your new password"
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <label
              htmlFor="confirmationCode"
              className="block text-sm font-medium text-gray-700"
            >
              Confirmation Code
            </label>
            <input
              id="confirmationCode"
              name="confirmationCode"
              type="text"
              required
              className={actionFormInputClassName}
              placeholder="Enter confirmation code"
              value={formState.confirmationCode}
              onChange={(e) =>
                setFormState({ ...formState, confirmationCode: e.target.value })
              }
            />
          </div>

          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700"
            >
              New Password
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              required
              className={actionFormInputClassName}
              placeholder="Enter new password"
              value={formState.newPassword}
              onChange={(e) =>
                setFormState({ ...formState, newPassword: e.target.value })
              }
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`${actionAuthSubmitButtonClassName} disabled:opacity-50`}
            >
              {isLoading ? 'Processing...' : 'Reset Password'}
            </button>
          </div>

          <div className="text-center text-sm">
            <Link
              to={portalPath('/login')}
              className={`font-medium ${actionLinkClassName}`}
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
